import React, { useMemo, useEffect, useCallback } from 'react';

import { SubscriptionCreate } from 'App/components/Subscriptions/SubscriptionsForm';
import RequestMail from 'App/components/Subscriptions/components/RequestMailDialog';
import { useGoogleDrive } from 'api/google/useGoogleDrive';

import { useProcessAsync, useChildComponent } from '@gogame/hooks-ts';
import { useScreenDispatch, useScreenState } from '@gogame/screens';

import {
  DIMENSION_CREATIVE,
  DIMENSION_CONCEPT,
  DIMENSION_END_CARD,
} from 'scenes/Trackers/Tracker/constants';
import {
  useLoadTrackerTableData,
  useLoadCohortTable,
  usePrefetchLoadMetrics,
  useLoadDrillTo,
  useLoadDimensions,
  useIsLongLoading,
} from 'scenes/Trackers/Tracker/data';
import { TRACKER_COHORT_PAGE, TRACKER_PAGE } from 'scenes/Trackers/constants';
import { useCurrentDimension } from 'scenes/Trackers/hooks';

import {
  useExportCohorts,
  useExportToGoogleDrive,
  useExportToGoogleDriveCohorts,
  useExportTrackerTable,
  useRequestMail,
} from '../../data/mutations';
import { useFiltersWithPeriodType, useTrackerData } from '../../hooks';
import { SET_DRILL_PATH, SET_NEXT_DIMENSION } from '../../reducers';
import TrackerToolbar, { CohortToolbar } from '../TrackerToolbar';
import CohortsDataTable from './CohortsDataTable';
import TrackerDataTable from './TrackerDataTable';
import { useColumnsWidths } from './hooks';

window.dataLayer = window.dataLayer || [];

const TrackerTableData = ({ presetName, isCohortsMode }) => {
  const screenState = useScreenState();
  const dimension = useCurrentDimension();
  const [loading, processAsync] = useProcessAsync();
  const { nextDimension } = screenState;

  const [childComponent, showChildComponent] = useChildComponent();

  const trackerData = useTrackerData();

  const [exportTableData] = useExportTrackerTable(trackerData);
  const [exportCohorts] = useExportCohorts();
  const [requestMail] = useRequestMail();

  const cohortPayload = {
    cohortsSettings: screenState.cohortsSettings,
    filtersCollection: screenState.filtersCollection,
    advancedFilters: screenState.advancedFilters,
    drillPath: screenState.drillPath,
    statuses: screenState.statuses,
    presentedRows: screenState.presentedRows,
    enabled: isCohortsMode,
  };

  const handleOpenSubscriptionsForm = () => {
    showChildComponent(SubscriptionCreate, {
      page: isCohortsMode ? TRACKER_COHORT_PAGE : TRACKER_PAGE,
      payload: isCohortsMode ? cohortPayload : trackerData,
    });
  };

  const handleExportWhenReady = () => {
    showChildComponent(RequestMail, {
      onSubmit: ({ subject }) => {
        requestMail({
          page: isCohortsMode ? TRACKER_COHORT_PAGE : TRACKER_PAGE,
          payload: isCohortsMode ? cohortPayload : trackerData,
          name: subject,
        });
      },
      subject: `[${presetName || `GG Tracker`}] Your report is ready`,
    });
  };

  const filtersCollectionWithPeriodType = useFiltersWithPeriodType(
    screenState.filtersCollection
  );

  const params = {
    filtersCollection: filtersCollectionWithPeriodType,
    tableSettings: screenState.tableSettings,
    drillPath: screenState.drillPath,
    columns: {
      order: screenState.columns.order,
    },
    dimensionSearchQuery: screenState.dimensionSearchQuery,
    advancedFilters: screenState.advancedFilters,
    forceReloadId: screenState.forceReloadId,
    presentedRows: screenState.presentedRows,
  };

  const {
    data: atData,
    isLoading: isTableDataLoading,
    isFetching: isTableDataFetching,
    loadingRowsIds,
  } = useLoadTrackerTableData(params, {
    enabled: !isCohortsMode,
    breakdownPath: screenState.breakdownPath,
    ready: screenState.ready,
  });

  const longLoading = useIsLongLoading();

  const [exportToGD] = useExportToGoogleDrive(trackerData);
  const [exportCohortsToGD] = useExportToGoogleDriveCohorts();

  usePrefetchLoadMetrics();

  const { data: cohortsData, isFetching: isCohortsLoading } =
    useLoadCohortTable(cohortPayload);
  const { data: drillTo, isLoading: isDrillToLoading } = useLoadDrillTo({
    drillPath: screenState?.changes?.drillPath || screenState.drillPath,
  });

  const isLoading = loading || isCohortsLoading;

  const disabledExport = !!(atData?.count > 500000);
  const { columnWidthGetter } = useColumnsWidths({
    compareMode: screenState.filtersCollection.length > 1,
  });
  const { data: dimensions } = useLoadDimensions({
    drillPath: screenState.drillPath,
  });
  const dimensionName = useMemo(() => {
    if (!dimension || !dimensions?.length) {
      return;
    }
    const item = dimensions?.find(({ id }) => dimension === id);
    return item ? item.name : '';
  }, [dimension, dimensions]);

  const breakdownTitle = dimensions?.length
    ? screenState.breakdownPath
        .map(
          ({ breakdownId }) =>
            dimensions.find(({ id }) => breakdownId === id)?.name || ''
        )
        .join(' / ')
    : undefined;

  const withDrillDown = useMemo(() => {
    return drillTo?.length > 0;
  }, [drillTo]);

  const isShowActions = useMemo(() => {
    return (
      [DIMENSION_CREATIVE, DIMENSION_CONCEPT, DIMENSION_END_CARD].includes(
        dimension
      ) ||
      screenState.breakdownPath.some(({ breakdownId }) =>
        [DIMENSION_CREATIVE, DIMENSION_CONCEPT, DIMENSION_END_CARD].includes(
          breakdownId
        )
      )
    );
  }, [dimension, screenState.breakdownPath]);

  const handleExportDataTable = useCallback(async () => {
    const { filtersCollection, drillPath, cohortsSettings, advancedFilters } =
      screenState.temporary || screenState;
    return isCohortsMode
      ? exportCohorts({
          filtersCollection,
          drillPath,
          cohortsSettings,
          advancedFilters,
        })
      : exportTableData();
  }, [isCohortsMode, exportCohorts, exportTableData, screenState]);

  const handleExportGoogleDrive = useCallback(
    ({ token }) => {
      const { filtersCollection, drillPath, cohortsSettings, advancedFilters } =
        screenState.temporary || screenState;

      isCohortsMode
        ? exportCohortsToGD({
            accessToken: token,
            filtersCollection,
            drillPath,
            cohortsSettings,
            advancedFilters,
          })
        : exportToGD({ accessToken: token });
    },
    [isCohortsMode, screenState]
  );
  const { onExport } = useGoogleDrive({
    onGetToken: handleExportGoogleDrive,
  });

  const dispatch = useScreenDispatch();

  const setNextDimension = (nextDimension) => {
    dispatch({
      type: SET_NEXT_DIMENSION,
      payload: nextDimension,
    });
  };

  useEffect(() => {
    if (drillTo?.length) {
      const nextDimensionExisted = drillTo.some(
        ({ id }) => id === nextDimension
      );
      if (!nextDimensionExisted) {
        setNextDimension(drillTo[0].id);
      }
    }
  }, [drillTo]);

  const handleChangeDrillPath = (_e, value) => {
    dispatch({
      type: SET_DRILL_PATH,
      payload: value,
    });
  };

  const [fullScreen, setFullScreen] = React.useState(false);

  const toolbar = useMemo(
    () => (
      <TrackerToolbar
        dimensions={dimensions}
        drillTo={drillTo}
        nextLevel={nextDimension}
        onChangeNextLevel={setNextDimension}
        onChangeDrillPath={handleChangeDrillPath}
        onExport={handleExportDataTable}
        onExportWhenReady={handleExportWhenReady}
        onOpenSubscriptionsForm={handleOpenSubscriptionsForm}
        onExportGoogleDrive={onExport()}
        disabledExport={disabledExport}
        disabledSearch={isLoading || isCohortsMode}
        disabledAdvancedFilters={isLoading || isCohortsMode}
        disabledCustomizeColumns={isCohortsMode}
        isDrillToLoading={isDrillToLoading}
        accentExport={longLoading}
        drillItems={atData?.drillItems}
        rows={atData?.rows}
      />
    ),
    [
      JSON.stringify(dimensions),
      JSON.stringify(drillTo),
      nextDimension,
      isCohortsMode,
      JSON.stringify(atData?.drillItems),
      JSON.stringify(atData?.rows),
      atData?.count,
      disabledExport,
      isDrillToLoading,
      isLoading,
      longLoading,
      JSON.stringify(screenState),
      screenState?.columns?.isAllScaled,
    ]
  );

  return (
    <React.Fragment>
      {isCohortsMode ? (
        <CohortsDataTable
          cohorts={cohortsData}
          fullScreen={fullScreen}
          onToggleFullScreen={setFullScreen}
          toolbar={
            <CohortToolbar
              dimensions={dimensions}
              drillTo={drillTo}
              nextLevel={nextDimension}
              onChangeNextLevel={setNextDimension}
              onChangeDrillPath={handleChangeDrillPath}
              onExport={handleExportDataTable}
              onExportWhenReady={handleExportWhenReady}
              onOpenSubscriptionsForm={handleOpenSubscriptionsForm}
              onExportGoogleDrive={onExport()}
              disabledExport={disabledExport}
              isDrillToLoading={isDrillToLoading}
              accentExport={longLoading}
              drillItems={atData?.drillItems}
            />
          }
          loading={isLoading}
        />
      ) : (
        <TrackerDataTable
          compareMode={atData?.compareMode}
          fullScreen={fullScreen}
          onToggleFullScreen={setFullScreen}
          presentedRows={screenState.presentedRows}
          totalCount={atData?.count}
          rows={atData?.rows}
          columns={atData?.columns}
          toolbar={toolbar}
          processAsync={processAsync}
          dimensionName={dimensionName}
          nextDimension={nextDimension}
          breakdownTitle={breakdownTitle}
          withDrillDown={withDrillDown}
          columnWidthGetter={columnWidthGetter}
          isShowActions={isShowActions}
          isLoading={isTableDataLoading}
          isFetching={isTableDataFetching}
          ready={screenState.ready}
          loadingRowsIds={loadingRowsIds}
          drillPath={screenState.drillPath}
          breakdownPath={screenState.breakdownPath}
          tableSettings={screenState.tableSettings}
          dimensionSearchQuery={screenState.dimensionSearchQuery}
          screenStateColumns={screenState.columns}
          screenFiltersCollection={screenState.filtersCollection}
          filtered={!!screenState.dimensionSearchQuery}
        />
      )}
      {childComponent}
    </React.Fragment>
  );
};

export default TrackerTableData;
